import { TranslateService } from '@ngx-translate/core';
import { ModalController, PopoverController } from '@ionic/angular';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, ViewChild} from '@angular/core';
import { IonNav } from '@ionic/angular';
import { MainCardViewDataModel } from '../../../models/MainCardViewDataModel';
import { MainToolTemplateDetail, MainToolTemplateListSelectionType } from '../../../models/mainToolTemplateDetail.model';
import { ChildNavNames, NavigationService, PageName } from '../../../services/navigation/navigation.service';
import { UIService } from '../../../services/ui/ui.service';
import { DeviceService } from '../../../services/device/device.service';
import {IndHeaderLeftDataModel} from "../../../models/indHeaderLeftDataModel";
import { AuthenticationService } from '@omni/services/authentication.service';
import { FooterService } from '@omni/services/footer/footer.service';
import { EventsService } from '@omni/services/events/events.service';
import { SampleService } from '@omni/services/sample/sample.service';
import { MultiSelectPopover } from '@omni/components/multi-select-popover/multi-select-popover';
import { Subject, takeUntil } from 'rxjs';
import { DynamicsClientService } from '@omni/data-services/dynamics-client/dynamics-client.service';
import { IndFormFieldViewDataModel } from '@omni/models/indFormFieldDataModel';
import { MainToolTemplateDeatilData } from '@omni/models/mainToolTemplateDetailData.model';
import { IndKeyControlsAreaModel } from '@omni/models/indKeyControlsAreaModel';
import { cloneDeep, isEmpty, xor, xorBy } from 'lodash-es';
const MIN_SEARCH_LENGTH = 1;

/**
 * The Main Tool Template component serves as a versatile tool for displaying and managing various data sets within the application. It provides a customizable interface that allows users to interact with data efficiently and intuitively.
 * Data Display: The component allows the display of data sets through the main card view, facilitating the visualization of key information.
 * Header Customization: Users can customize the header by specifying the title and divider title, as well as toggling the visibility of left and right header buttons.
 * Header Buttons: Left and right header buttons can be configured with text or images, providing additional functionality such as navigation or action triggers.
 * Search Functionality: The component supports search functionality, enabling users to filter data based on specific criteria. Users can specify the search title and minimum search length according to their requirements.
 * List Selection: List selection can be enabled to allow users to select items from a list. Various selection types are available, and options can be updated for team members if required.
 * Confirmation Alerts: Confirmation alerts can be displayed to users for actions that require confirmation. Customizable data for the confirmation alert is supported.
 * Event Handling: Users can define event handlers to manage interactions and data manipulation within the component. Event handlers receive relevant data and event targets for processing.
 * Search Handler: Custom search handlers can be defined to process search queries and return filtered results.
 * @Component
 */
@Component({
  selector: 'main-tool-template[base-page]',
  templateUrl: 'main-tool-template.html',
  styleUrls:['main-tool-template.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainToolTemplateComponent {

    @Input() viewData:MainToolTemplateDetail;
    @Input() formData:any;
    public searchInput:string;
    public listData:Array<MainCardViewDataModel> = [];
    public filteredData:Array<MainCardViewDataModel> = [];
    public itemsToDisplay:Array<MainCardViewDataModel> = [];
    public selectedItems:Array<MainCardViewDataModel> = [];
    private tempSelectedItems:Array<MainCardViewDataModel> = [];
    public searchActive:boolean = false;
    indHeaderLeftModel: IndHeaderLeftDataModel;
    @Input() isNavWithinModalView: boolean;
    @Input() navWithinModalRef: any;
    @Input() isGroupedView:boolean = false;
    public childrenLength:number;
    @Input() viewDataParam:MainToolTemplateDetail;
    @ViewChild('maintooltemplaterightpane', {static: true}) public navCtrl: IonNav;
    sectionHeader: { id: string; title: string; controls: any[]; };
    @Input() isHierarchyView:boolean = false;
    @Input() disableSorting:boolean = false;
    totalLength: number = 0;
    private preventDeSelect = false;
    private recordCount: number = 30;
    private storedListData:Array<MainCardViewDataModel> = [];
    private readonly $ngDestroy: any = new Subject<boolean>();
    public isSelectedSectionExpanded: boolean = true;
    public enableKeyControlArea: boolean = false;
    public mainToolTemplateKeyControlAreaModel:IndKeyControlsAreaModel;
    public isSearchButtonDirty: boolean = false;
    @Input() searchTextFromComponent: string = '';

    constructor(
        public navService: NavigationService,
        public uiService : UIService,
        private device : DeviceService,
        private modalCtrl : ModalController,
        public translate: TranslateService,
        private readonly authService: AuthenticationService,
        public navControl: IonNav,
        public footerService: FooterService,
        public events: EventsService,
        public sampleService: SampleService,
        private popover: PopoverController,
        private dynamics: DynamicsClientService,
        public _cd: ChangeDetectorRef,
    ){
    }

    ngAfterViewInit(){
        if(this.viewDataParam){
            this.viewData = this.viewDataParam;
            this.initListData();
        }
    }

  ngOnInit() {
    this.initListData();
    if(this.enableKeyControlArea) {
      this.isSearchButtonDirty = false;
      this.initKeyControlsAreaModel();
    }
    this.navService.initRightPaneChildNav(this.navCtrl, ChildNavNames.AccountPageNavigation, PageName.AccountPageComponent, false, { deviceString: this.device.deviceFlags.ios ? 'ios' : 'other' });
    this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView, PageName.NothingSelectedView, { notShowText: true });
      this.initSectionHeader();
    this.events.observe('updateMainToolTemplateList').pipe(takeUntil(this.$ngDestroy)).subscribe((updatedList: MainToolTemplateDetail) => {
      this.initSelectedAreaForFilterSelection(updatedList);
      this._cd.markForCheck();
    });
  }

  initSectionHeader(){
    let sectionHeader: any[] = [];
    sectionHeader.push({
      id: 'clear-all',
      text: `${this.translate.instant('CLEAR_ALL')}`,
      isDisabled: !(this.selectedItems.some(item => item.showEndIcon === true)),
    });
    if(this.viewData.isSelectedSectionCollapsible) {
      sectionHeader.push({
        id: 'selected-section-collapsible',
        icon: this.isSelectedSectionExpanded ? 'assets/imgs/up-arrow.svg' : 'assets/imgs/down-arrow.svg',
        isDisabled: false
      })
    }
    this.sectionHeader = {
      id: 'select-all-header',
      title: this.translate.instant('SELECTED') + ' (' + this.selectedItems.length + ')',
      controls: sectionHeader
    };
  }

  onSectionHeaderControlClick(type: string){
    switch (type) {
      case 'clear-all':
        this.clearAll();
        break;
      case 'selected-section-collapsible':
          this.isSelectedSectionExpanded = !this.isSelectedSectionExpanded;
          this.initSectionHeader();
        break;
      default:
        break;
    }
  }

  private initSelectedAreaForFilterSelection(updatedList: MainToolTemplateDetail) {
    let existingSelectedItems = cloneDeep(this.selectedItems);
      let existingTempSelectedItems = cloneDeep(this.tempSelectedItems);
      let diff = xorBy(existingSelectedItems, existingTempSelectedItems, 'id');
      this.viewData = updatedList;
      this.initListData();
      if(this.enableKeyControlArea) this.initKeyControlsAreaModel();
      if(!isEmpty(existingSelectedItems)) {
        existingSelectedItems.forEach(item => {
          let foundListItem = this.listData.find(d => d.id == item.id);
          if(foundListItem) {
            foundListItem.isItemSelectedForSelectionView = true;
            foundListItem.endIconType = 'indegene-selectors-checkmark-icon';
            foundListItem.endIconCssClass = 'checkmark-icon';
          };
          let foundFilteredItem = this.filteredData.find(d => d.id == item.id);
          if(foundFilteredItem) {
            foundFilteredItem.isItemSelectedForSelectionView = true;
            foundFilteredItem.endIconType = 'indegene-selectors-checkmark-icon';
            foundFilteredItem.endIconCssClass = 'checkmark-icon';
          };
        })
        if(!isEmpty(diff)) diff.forEach(item => {
          let foundListItem = this.listData.find(d => d.id == item.id);
          if(foundListItem) {
            foundListItem.isItemSelectedForSelectionView = false;
            foundListItem.endIconType = 'indegene-selectors-add-icon';
            foundListItem.endIconCssClass = 'add-icon';
          };
          let foundFilteredItem = this.filteredData.find(d => d.id == item.id);
          if(foundFilteredItem) {
            foundFilteredItem.isItemSelectedForSelectionView = false;
            foundFilteredItem.endIconType = 'indegene-selectors-add-icon';
            foundFilteredItem.endIconCssClass = 'add-icon';
          };
        })
        this.itemsToDisplay = this.sliceItems(0, this.recordCount);
      } else this.clearAll();
      this.selectedItems = existingSelectedItems;
      this.tempSelectedItems = existingTempSelectedItems;
      this.initSelectListHeaderLeft();
      this.initSectionHeader();
  }

  private initKeyControlsAreaModel() {
    this.mainToolTemplateKeyControlAreaModel = {
      id:'main-tool-template-controls-area',
      isHidden: this.viewData.isFor != 'setBookingProducts' && this.viewData.isFor != 'medicalInquiryProducts',
      eventHandler: (id: string, event, eventName) => this.handleControlsAreaEvent(id, event, eventName),
      controls:[
        {
          id:'search',
          text: this.translate.instant('SEARCH'),
          isHidden: this.isSearchButtonDirty,
          isDisabled:!this.searchActive,
          leftAligned: true
        },
        {
          id:'search-others',
          text: this.viewData && this.viewData.title ? this.translate.instant(`SEARCH_OTHER_${(this.viewData.title).toUpperCase()}`) : this.translate.instant('SEARCH_OTHERS'),
          isHidden: !this.isSearchButtonDirty,
          isDisabled: !this.searchActive,
          leftAligned: false
        },
        {
          id:'clear-search',
          text: this.translate.instant('CLEAR'),
          isHidden: true,
          isDisabled:!this.searchActive,
          leftAligned: true
        }        
      ]
    };
  }

  private handleControlsAreaEvent(id,event,eventName){
    if(id && eventName && eventName == 'controls-click'){
      switch(id){
        case 'search':
          this.isSearchButtonDirty = true;
          this.search();
          break;
        case 'search-others':
          this.search();
          break;
        case 'clear-search':
          this.searchInput = '';
          this.searchActive = false;
          this.search();
        default:
          console.log('Unhandled switch case statement');
      }
    }
  }

  public search() {
    if(this.viewData.searchButtonHandler) {
      this.viewData.searchButtonHandler(this.searchInput);
    }
    this.initKeyControlsAreaModel();
  }

  public clearAll() {
    this.selectedItems = this.selectedItems.filter((selectedItem, index) =>{
      if(selectedItem.showEndIcon){
        let foundRemovedItemIdx = this.filteredData.findIndex(filterItem => filterItem.id == selectedItem.id);
        if (foundRemovedItemIdx >= 0) {
          let selectorItem: MainCardViewDataModel = {
             ...this.filteredData[foundRemovedItemIdx],
             isItemSelectedForSelectionView: false,
             endIconType: 'indegene-selectors-add-icon',
             endIconCssClass: 'add-icon',
             isMainToolTemplate: true,
             noSeparationLine: false,
             showQtyControl: false,
             qty: 1,
             clickHandler: (id: string, event, target, data) => this._handleSelectionEvents(id, event, target, data),
          }
          this.filteredData.splice(foundRemovedItemIdx, 1, selectorItem);
        }
        let foundMainRemovedItemIdx = this.listData.findIndex(item => item.id == selectedItem.id);
        if (foundMainRemovedItemIdx >= 0) {
          let selectorItem: MainCardViewDataModel = {
             ...this.listData[foundMainRemovedItemIdx],
             isItemSelectedForSelectionView: false,
             endIconType: 'indegene-selectors-add-icon',
             endIconCssClass: 'add-icon',
             isMainToolTemplate: true,
             noSeparationLine: false,
             showQtyControl: false,
             qty: 1,
             clickHandler: (id: string, event, target, data) => this._handleSelectionEvents(id, event, target, data),
          }
          this.listData.splice(foundMainRemovedItemIdx, 1, selectorItem);
        }
     } else {
       return (!selectedItem.showEndIcon);
     }
     if(this.isHierarchyView){
      try {
        let foundAddedItem = this.findHierarchyItem(selectedItem.id, selectedItem, this.filteredData);
        let foundMainAddedItem = this.findHierarchyItem(selectedItem.id, selectedItem, this.listData);
        foundAddedItem.endIconType = 'indegene-selectors-add-icon';
        foundAddedItem.endIconCssClass = 'add-icon';
        foundMainAddedItem.endIconType = 'indegene-selectors-add-icon';
        foundMainAddedItem.endIconCssClass = 'add-icon';
      } catch (error) {
        console.log(error);
      }
     }
     });
     this.itemsToDisplay = this.sliceItems(0, this.recordCount);
     this.initSelectListHeaderLeft();
     this.initSectionHeader();
     this._cd.detectChanges();
  }

  ngOnDestroy() {
    if(this.navService.getCurrentPageName() === PageName.GoAppActivitiesPageComponent) {
      //navigated from meeting then set right pane
      this.uiService.showRightPane = true;
    }
    this.navService.popChildNavCtrlFromStack(ChildNavNames.AccountPageNavigation);
  }

    private initListData():void {
        if(this.viewData){
            if(this.viewData.isListSelectionEnabled){
                if(this.viewData.updateOptionForTeamMember){
                  if (!isEmpty(this.viewData.data)) {
                    this.viewData.data.forEach(item => {
                      if (!isEmpty(item)) {
                        let selectorItem:MainCardViewDataModel = {
                          ...item,
                          isMainToolTemplate: true,
                          noSeparationLine: false,
                          clickHandler: (id: string, event,target,data) => this._handleSelectionEvents(id,event,target,data),
                        }
                        if(this.viewData.passOverLabelCssToMainCard) selectorItem.labelItemCssClass = this.viewData.passOverLabelCssToMainCard;
                        if(!selectorItem.unMappedProduct){
                            this.listData.push(selectorItem);
                        }
                        if(item.isItemSelectedForSelectionView && item.eventOwnerId && (item.eventOwnerId == this.authService.user.systemUserID)){
                            let selectedItem:MainCardViewDataModel = {
                                ...selectorItem,
                                endIconType: 'indegene-selectors-remove-icon',
                                endIconCssClass: 'remove-icon',
                                isMainToolTemplate: true,
                                noSeparationLine: false,
                                clickHandler: (id: string, event,target,data) => this._handleSelectionEvents(id,event,target,data),
                            }
                            if(this.viewData.passOverLabelCssToMainCard) selectorItem.labelItemCssClass = this.viewData.passOverLabelCssToMainCard;
                            this.selectedItems.push(selectedItem);
                            this.tempSelectedItems.push(selectedItem);
                        } else if(item.isItemSelectedForSelectionView  && item.eventOwnerId && (item.eventOwnerId != this.authService.user.systemUserID)) {
                            let selectedItem:MainCardViewDataModel = {
                                ...selectorItem,
                                showEndIcon: false,
                                isMainToolTemplate: true,
                                noSeparationLine: false,
                                clickHandler: (id: string, event,target,data) => this._handleSelectionEvents(id,event,target,data),
                            }
                            if(this.viewData.passOverLabelCssToMainCard) selectorItem.labelItemCssClass = this.viewData.passOverLabelCssToMainCard;
                            this.selectedItems.push(selectedItem);
                            this.tempSelectedItems.push(selectedItem);
                        }
                        else if (item.isItemSelectedForSelectionView  && !item.eventOwnerId ){
                            let selectedItem:MainCardViewDataModel = {
                                ...selectorItem,
                                endIconType: 'indegene-selectors-remove-icon',
                                endIconCssClass: 'remove-icon',
                                isMainToolTemplate: true,
                                noSeparationLine: false,
                                clickHandler: (id: string, event,target,data) => this._handleSelectionEvents(id,event,target,data),
                            }
                            if(this.viewData.passOverLabelCssToMainCard) selectorItem.labelItemCssClass = this.viewData.passOverLabelCssToMainCard;
                            this.selectedItems.push(selectedItem);
                            this.tempSelectedItems.push(selectedItem);
                        }
                      }
                    });
                  }
                } else {
                this.listData = [];
                this.selectedItems = [];
                this.tempSelectedItems = [];
                if (!isEmpty(this.viewData.data)) {
                  this.viewData.data.forEach(item => {
                    if (!isEmpty(item)) {
                      let selectorItem:MainCardViewDataModel = {
                        ...item,
                        isMainToolTemplate: true,
                        noSeparationLine: false,
                        clickHandler: (id: string, event,target,data) => this._handleSelectionEvents(id,event,target,data),
                      }
                      if(this.viewData.passOverLabelCssToMainCard) selectorItem.labelItemCssClass = this.viewData.passOverLabelCssToMainCard;
                      if( selectorItem.childItems && selectorItem.childItems.length > 0) {
                        selectorItem.noLeftMarginLabel = true;
                        selectorItem.showIcon = true;
                        selectorItem.iconName = 'chevron-down-outline';
                        selectorItem.isExpanded = true;
                        selectorItem.childItems.forEach(o=>{
                          o.isMainToolTemplate = true;
                          o.noSeparationLine = false;
                          o.clickHandler = (id: string, event,target,data) => this._handleSelectionEvents(id,event,target,data)
                        })
                      }
                      this.listData.push(selectorItem);
                      //construct selected Items array
                      if(!this.isHierarchyView && item.isItemSelectedForSelectionView){
                          let selectedItem:MainCardViewDataModel = {
                              ...selectorItem,
                              endIconType: 'indegene-selectors-remove-icon',
                              endIconCssClass: 'remove-icon',
                              isMainToolTemplate: true,
                              noSeparationLine: false,
                              noLeftMarginLabel: this.sampleService.isHandleElligibleProductSelect ? false : selectorItem.noLeftMarginLabel,
                              showIcon: this.sampleService.isHandleElligibleProductSelect ? false : selectorItem.showIcon,
                              iconName: this.sampleService.isHandleElligibleProductSelect ? '' : selectorItem.iconName,
                              isExpanded: this.sampleService.isHandleElligibleProductSelect ? false : selectorItem.isExpanded,
                              clickHandler: (id: string, event,target,data) => this._handleSelectionEvents(id,event,target,data),
                          }
                          if(this.viewData.passOverLabelCssToMainCard) selectorItem.labelItemCssClass = this.viewData.passOverLabelCssToMainCard;
                          if(item.isQtyControl) selectedItem.showQtyControl = true;
                          this.selectedItems.push(selectedItem);
                          this.tempSelectedItems.push(selectedItem);
                      }
                    }
                  });
                }
            }
            }else{
                this.listData = this.viewData?.data || [];
                this.selectedItems = [];
                if (!isEmpty(this.viewData.data)) {
                  this.viewData.data.forEach(item => {
                    if (!isEmpty(item)) {
                      if(!this.isHierarchyView && item.isItemSelectedForSelectionView){
                        if(item.isQtyControl) item.showQtyControl = true;
                        this.selectedItems.push(item);
                    } 
                    }
                  });
                }
            }
            this.storedListData = cloneDeep(this.listData);
            this.filteredData = this.listData;
            this.itemsToDisplay = this.sliceItems(0, this.recordCount);
            this.initSelectListHeaderLeft();
              this.initSectionHeader();
            if(!this.isGroupedView && !this.isHierarchyView && !this.disableSorting) this._sortItemsList(true);
            else if(this.isGroupedView) this.childrenLength = this.filteredData.filter(o=>o.showEndIcon).length;
            else this.totalLength = this.getLengthRecursively(this.filteredData);
            if(this.isHierarchyView){
              this.getSelectedItemsRecursively(this.listData);
            }
            if(this.viewData.enableKeyControlArea) this.enableKeyControlArea = true;
            this.preventDeSelect = !!this.viewData.preventDeSelect;
            try {
              setTimeout(()=> {
                this.uiService.dismissLoader();
              },200)
            } catch (error) {}
        }
        if (this.searchTextFromComponent) {
          this.searchText(null);
        }
    }

    private _sortItemsList(isInitial:boolean = false){
        let sortByPropertyName:string;
        if(this.filteredData && this.filteredData.length != 0){
            if(this.filteredData[0].primaryTextRight){
                sortByPropertyName = 'primaryTextRight';
            }else{
                sortByPropertyName = 'primaryTextLeft';
            }
        }
        if(this.viewData && this.viewData.orderByPropertyName){
            sortByPropertyName = this.viewData.orderByPropertyName
        }
        if(sortByPropertyName){
            if(this.filteredData.length !== 0){
                this.filteredData = this.filteredData.sort((a,b)=> (a[sortByPropertyName]?.toLowerCase()>b[sortByPropertyName]?.toLowerCase()?1:-1));
            }
            if(this.selectedItems.length !== 0){
                this.selectedItems = this.selectedItems.sort((a,b)=> (a[sortByPropertyName]?.toLowerCase()>b[sortByPropertyName]?.toLowerCase()?1:-1));
            }
            if(this.listData.length !== 0 && isInitial){
                this.listData = this.listData.sort((a,b)=> (a[sortByPropertyName]?.toLowerCase()>b[sortByPropertyName]?.toLowerCase()?1:-1));
            }
        }
        this.itemsToDisplay = this.sliceItems(0, this.recordCount);
    }

    private _handleAddSelectionEvent(data,id) {
        data.endIconType = 'indegene-selectors-checkmark-icon';
        data.endIconCssClass = 'checkmark-icon';
        let foundAddedItem;
        let foundMainAddedItem;
        if(!this.isHierarchyView){
          foundAddedItem = this.filteredData.find(item => item.id == id && (!data.parentID || data.parentID == item.parentID));
          foundMainAddedItem = this.listData.find(item => item.id == id && (!data.parentID || data.parentID == item.parentID));
        }
        else{
          foundAddedItem = this.findHierarchyItem(id, data, this.filteredData);
      
          foundMainAddedItem = this.findHierarchyItem(id, data, this.listData);
         
        }
        if (foundAddedItem) {
          data.endIconType = 'indegene-selectors-checkmark-icon';
          data.endIconCssClass = 'checkmark-icon';
        }
        if (foundMainAddedItem) {
          data.endIconType = 'indegene-selectors-checkmark-icon';
          data.endIconCssClass = 'checkmark-icon';
        }
        let selectorItem: MainCardViewDataModel = {
            ...foundMainAddedItem,
            endIconType: 'indegene-selectors-remove-icon',
            endIconCssClass: 'remove-icon',
            isMainToolTemplate: true,
            noSeparationLine: false,
            clickHandler: (id: string, event, target, data) => this._handleSelectionEvents(id, event, target, data),
        }
        if(this.viewData.passOverLabelCssToMainCard) selectorItem.labelItemCssClass = this.viewData.passOverLabelCssToMainCard;
        if(foundMainAddedItem.isQtyControl) selectorItem.showQtyControl = true;
        selectorItem.showIcon = false;
        selectorItem.noLeftMarginLabel = false;
        this.selectedItems.push(selectorItem);
        this.initSectionHeader();
    }

    private _handleRemoveSelectionEvent(id, data?) {
        let idx = this.selectedItems.findIndex(item => item.id == id && (!data.parentID || data.parentID == item.parentID));
        if (idx >= 0) {
            this.selectedItems.splice(idx, 1);
        }
        if(!this.isHierarchyView){
          let foundRemovedItemIdx = this.filteredData.findIndex(item => item.id == id && (!data.parentID || data.parentID == item.parentID));
          if (foundRemovedItemIdx >= 0) {
              let selectorItem: MainCardViewDataModel = {
                  ...this.filteredData[foundRemovedItemIdx],
                  endIconType: 'indegene-selectors-add-icon',
                  endIconCssClass: 'add-icon',
                  isMainToolTemplate: true,
                  noSeparationLine: false,
                  showQtyControl: false,
                  clickHandler: (id: string, event, target, data) => this._handleSelectionEvents(id, event, target, data),
              }
              this.filteredData.splice(foundRemovedItemIdx, 1, selectorItem);
          }
          let foundMainRemovedItemIdx = this.listData.findIndex(item => item.id == id && (!data.parentID || data.parentID == item.parentID));
          if (foundMainRemovedItemIdx >= 0) {
              let selectorItem: MainCardViewDataModel = {
                  ...this.listData[foundMainRemovedItemIdx],
                  endIconType: 'indegene-selectors-add-icon',
                  endIconCssClass: 'add-icon',
                  isMainToolTemplate: true,
                  noSeparationLine: false,
                  showQtyControl: false,
                  clickHandler: (id: string, event, target, data) => this._handleSelectionEvents(id, event, target, data),
              }
              this.listData.splice(foundMainRemovedItemIdx, 1, selectorItem);
          }
          this.itemsToDisplay = this.sliceItems(0, this.recordCount);
        }
        else{
          let foundAddedItem = this.findHierarchyItem(id, data, this.filteredData);
          let foundMainAddedItem = this.findHierarchyItem(id, data, this.listData);
          foundAddedItem.endIconType = 'indegene-selectors-add-icon';
          foundAddedItem.endIconCssClass = 'add-icon';
          foundMainAddedItem.endIconType = 'indegene-selectors-add-icon';
          foundMainAddedItem.endIconCssClass = 'add-icon';
          foundAddedItem.showQtyControl = false;
          foundMainAddedItem.showQtyControl = false;
        }
        this.initSectionHeader();
    }

    private _handleAddQtyEvent(id, data?) {
      let foundSelectedItem = this.selectedItems.find(item => item.id == id && (!data.parentID || data.parentID == item.parentID));
      let foundfilteredItem = this.filteredData.find(item => item.id == id && (!data.parentID || data.parentID == item.parentID));
      // let foundlistItem = this.listData.find(item => item.id == id && (!data.parentID || data.parentID == item.parentID));
      if(foundSelectedItem) foundSelectedItem.qty += 1;
      if(foundfilteredItem) foundfilteredItem.qty += 1;
      // if(foundlistItem) foundlistItem.qty += 1;
    }

    private _handleRemoveQtyEvent(id, data?) {
      let foundSelectedItem = this.selectedItems.find(item => item.id == id && (!data.parentID || data.parentID == item.parentID));
      let foundfilteredItem = this.filteredData.find(item => item.id == id && (!data.parentID || data.parentID == item.parentID));
      // let foundlistItem = this.listData.find(item => item.id == id && (!data.parentID || data.parentID == item.parentID));
      if(foundSelectedItem) foundSelectedItem.qty = Math.max(foundSelectedItem.qty-1, 1);
      if(foundfilteredItem) foundfilteredItem.qty = Math.max(foundfilteredItem.qty-1, 1);
      // if(foundlistItem) foundlistItem.qty = Math.max(foundlistItem.qty-1, 0);
    }

    private _handleInputQtyEvent(id, data, input) {
      let foundSelectedItem = this.selectedItems.find(item => item.id == id && (!data.parentID || data.parentID == item.parentID));
      let foundfilteredItem = this.filteredData.find(item => item.id == id && (!data.parentID || data.parentID == item.parentID));
      // let foundlistItem = this.listData.find(item => item.id == id && (!data.parentID || data.parentID == item.parentID));
      if(foundSelectedItem) foundSelectedItem.qty = input;
      if(foundfilteredItem) foundfilteredItem.qty = input;
      // if(foundlistItem) foundlistItem.qty = input;
    }
 
    private _handleSelectionEvents(id,event,target,data:MainCardViewDataModel){
        if(id && target && target == 'endIcon'){
          if(data && data.showEndIcon){
            switch (data.endIconType){
              case 'indegene-selectors-add-icon':
                if(this.viewData.listSelectionType == MainToolTemplateListSelectionType.SINGLESELECTION && this.selectedItems && this.selectedItems.length != 0){
                    this._handleRemoveSelectionEvent(this.selectedItems[0].id, this.selectedItems[0]);
                }
                this._handleAddSelectionEvent(data,id);
                break;
              case 'indegene-selectors-remove-icon':
                this._handleRemoveSelectionEvent(id, data);
                break;
            }
          }
          this.initSelectListHeaderLeft();
          if(!this.isGroupedView && !this.disableSorting) this._sortItemsList();
        }
        if(id && target && target == 'addQty') {
          if(data) this._handleAddQtyEvent(id, data);
          this.initSelectListHeaderLeft();
        }
        if(target && target=='mainItem'){
          this.listData.forEach(data=>{
            data.isSelected = false;
            if(data.id === id) data.isSelected = true;
          });
          this.filteredData.forEach(data=>{
            data.isSelected = false;
            if(data.id === id) data.isSelected = true;
          });
        }
        if(id && target && target == 'numQty' && event && event.target && event.target.value) {
          if(data) this._handleInputQtyEvent(id, data, parseInt(event.target.value));
          this.initSelectListHeaderLeft();
        } else if(id && target && target == 'removeQty') {
          if(data) this._handleRemoveQtyEvent(id, data);
          this.initSelectListHeaderLeft();
        } else if(target && target=='startIcon'){
          data.isExpanded = !data.isExpanded;
          data.iconName = data.isExpanded?'chevron-down-outline':'chevron-forward-outline'
        } else if(target && target=='mainItem' && this.viewData.isDetailPageEnabled) {
          this.initDetailData(data);
        } else if(this.viewData && this.viewData.eventsHandler){
            let passedData = {
                id: id,
                event: event,
                target: target,
                data : data,
            };
            this.viewData.eventsHandler(passedData,`MainItemEvents-${target}`,this.viewData);
        }
      }

    public searchText(ev):void {
        let val:string = (ev && ev.target && ev.target.value) ? ev.target.value : '';
        if (!ev && this.searchTextFromComponent) {
          val = this.searchTextFromComponent;
          this.searchInput = val;
        }
        if(this.viewData.minSearchLength){
            if(val.length >= this.viewData.minSearchLength){
                this.doSearch(val);
                 this.searchActive = true;
            }else {
                this.filteredData = this.listData;
                if(this.isHierarchyView) this.totalLength = this.getLengthRecursively(this.filteredData);
                this.searchActive = false;
            }
        }else if(val.length >= MIN_SEARCH_LENGTH){
            this.doSearch(val);
             this.searchActive = true;
        }else {
            this.filteredData = this.listData;
            if(this.isHierarchyView) this.totalLength = this.getLengthRecursively(this.filteredData);
            this.searchActive = false;
        }
        this.itemsToDisplay = this.sliceItems(0, this.recordCount);
        if(this.enableKeyControlArea) this.initKeyControlsAreaModel();
    }

    public clearSearch(ev){
      this.filteredData = this.listData;
      if(this.isHierarchyView) this.totalLength = this.getLengthRecursively(this.filteredData);
      if(this.recordCount < 30) this.itemsToDisplay = this.sliceItems(0, 30);
      else this.itemsToDisplay = this.sliceItems(0, this.recordCount);
      this.searchActive = false;
    }

    private doSearch(val:string){
        if(this.viewData.searchHandler){
          let ids:string[] = this.viewData.searchHandler(val);
          if(this.viewData.title == this.translate.instant("SEARCH_ELIGIBLE_PRODUCTS")){
            this.filteredData = this.listData.filter( data => {
                return ids.some(str=> str.concat('_').concat(data.skuId) == data.id);
            });
          }else{
            this.filteredData = this.listData.filter( data => {
                return ids.some(str=> str == data.id);
            });
          }
        }else{
          if(!this.isHierarchyView){
            this.filteredData = this.listData.filter( data => {
                let str:string = '';
                if(data.primaryTextLeft) str += data.primaryTextLeft.trim();
                if(data.primaryTextRight) str += data.primaryTextRight.trim();
                if(data.secondaryTextRight) str += data.secondaryTextRight.trim();
                return str.toLowerCase().includes(val.toLowerCase());
            });
          }
          else{
            this.filteredData = this.searchRecursively(val, this.listData);
            this.totalLength = this.getLengthRecursively(this.filteredData);
          }
        }
    }
  
  public changeTab(ev) {
    if (this.viewData.tabChangeHandler) {
      this.viewData = this.viewData.tabChangeHandler(ev);
      this.clearSearch('');
      this.initListData();
      this.searchInput = '';
    }
  }

    public get noChanges(): boolean {
      let isQtySame = this.selectedItems.every(s => this.storedListData.some(l => s.id == l.id && s.qty == l.qty));
      if (
        this.preventDeSelect
        && this.tempSelectedItems.length > 0
        && this.selectedItems.length === 0
      ) {
        // DeSelection is not allowed
        return true && isQtySame;
      }
      if(!this.isGroupedView && !this.isHierarchyView) return (xor(this.selectedItems.map(item=> item.id),this.tempSelectedItems.map(item=> item.id)).length === 0) && isQtySame;
      else if(!this.isHierarchyView){
        let selectedIDs = this.selectedItems.map(item=> 'id_'+item.id +'parent_'+item.parentID)
        let tempSelectedIDs = this.tempSelectedItems.map(item=> 'id_'+item.id +'parent_'+item.parentID)
        return (xor(selectedIDs,tempSelectedIDs).length === 0) && isQtySame;
      }
      else{
        let selectedIDs = this.selectedItems.map(item=> 'id_'+item.id +'parent_'+item.parentID+'uniqueHierarchyIdentifier_'+item.uniqueHierarchyIdentifier)
        let tempSelectedIDs = this.tempSelectedItems.map(item=> 'id_'+item.id +'parent_'+item.parentID+'uniqueHierarchyIdentifier_'+item.uniqueHierarchyIdentifier)
        return (xor(selectedIDs,tempSelectedIDs).length === 0) && isQtySame;
      }
    }

    public async goBack(flag:boolean){
        let data;
        if (!flag) {
          this.events.publish('updateMandatoryField', PageName.MainToolTemplateComponent);
        }
        if(flag){
            let removedItems:MainCardViewDataModel[] = this.tempSelectedItems.filter((c) =>!this.selectedItems.some(t => t.id == c.id));
            if(this.viewData && this.viewData.confirmationCheckRequired){
                await this.uiService.displayAlertConfirmation(this.viewData.confirmationAlertData).then(info => {
                    data = {
                        selectedItems: this.selectedItems,
                        removedItems: removedItems,
                        isDone:true,
                    };
                }).catch(error => {
                    // Cancel button Action
                });
            }else{
                data = {
                    selectedItems: this.selectedItems,
                    removedItems: removedItems,
                    isDone:true,
                };
            }
        }
        //this.viewCtrl.dismiss(data, null, this.viewData.navOptions);
        if(this.viewData && this.viewData.eventsHandler){
            this.viewData.eventsHandler(data,(flag)?'RightHeaderButtonClick':'LeftHeaderButtonClick',this.viewData);
        }
        if (this.isNavWithinModalView) {
            if (document.getElementsByTagName('ion-modal')[0].classList.contains('fullStretchView')) {
                document.getElementsByTagName('ion-modal')[0].classList.remove('fullStretchView');
            }
            if (this.navService.getCurrentMasterPageName() === PageName.MainToolTemplateComponent) {
                this.navService.popWithPageTracking({ progressAnimation: false, animate: false }, this.navControl);
            } else {
                this.navControl.pop({ progressAnimation: false }).then(() => {});
            }
        } else if(this.navService.getCurrentMasterPageName() === PageName.MainToolTemplateComponent){
            this.navService.popWithPageTracking();
        } else{
          this.modalCtrl.dismiss(data, null, this.viewData.navOptions);
        }
    }
    public onPageTitleControlClick(id: string) {
      switch (id) {
        case 'cancel':
          this.goBack(false);
          break;
        case 'done':
          this.goBack(true);
          break;
        default:
          console.log("Unhandled switch case statement");
          break;
      }
    }

    private initSelectListHeaderLeft(): void {
      let buttons = [];
      if(this.viewData.showLeftHeaderButton) {
        buttons.push({
            id: "cancel",
            imgSrc: this.viewData.leftHeaderBtnImgSrc ? this.viewData.leftHeaderBtnImgSrc : '',
            cssClass: 'seventyPercentWidth',
            name: this.viewData.leftHeaderBtnText ? this.viewData.leftHeaderBtnText : this.translate.instant('CANCEL'),
            isDisabled: false,
            align: "left",
          },
        );
      }
      if(this.viewData.showRightHeaderButton) {
        buttons.push({
          id: "done",
          imgSrc: this.viewData.rightHeaderBtnImgSrc ? this.viewData.rightHeaderBtnImgSrc : '',
          cssClass: 'seventyPercentWidth',
          name: this.viewData.rightHeaderBtnText ? this.viewData.rightHeaderBtnText : this.translate.instant('DONE'),
          isDisabled: this.noChanges,
          align: "right",
          }
        );
      }
      this.indHeaderLeftModel = {
        id: 'main-tool-template-header-left',
        cssClass: 'main-tool-header-title',
        title: this.viewData ? this.viewData.title : '',
        mode: false,
        customHeaderProps: { hasCancel: true },
        controls: buttons,
      };

      if(this.viewData && this.viewData.searchTitle){
        this.indHeaderLeftModel.title = this.viewData.searchTitle;
      }
    }

    private findHierarchyItem(id, data, masterList){
      let foundAddedItem;
      masterList.forEach(item => {
        if(!foundAddedItem){
          if(item.id == id
            && (!data.parentID || data.parentID == item.parentID)
            && (!data.uniqueHierarchyIdentifier || data.uniqueHierarchyIdentifier == item.uniqueHierarchyIdentifier)
          ) foundAddedItem = item;
          if(!foundAddedItem && item.childItems && item.childItems.length){
            foundAddedItem = this.findHierarchyItem(id, data, item.childItems);
          }
        }
      });
      return foundAddedItem;
    }

    private searchRecursively(val:string, list:MainCardViewDataModel[]){
      let matchedResultsCurrentLevel = [];
      list.forEach(data=>{
        if(!data.childItems || !data.childItems.length) {
          let str:string = '';
          if(data.primaryTextLeft) str += data.primaryTextLeft.trim();
          if(data.primaryTextRight) str += data.primaryTextRight.trim();
          if(str.toLowerCase().includes(val.toLowerCase())) matchedResultsCurrentLevel.push(data);
        }
        else {
          let matchedChildRes =  this.searchRecursively(val, data.childItems);
          let _data:MainCardViewDataModel = JSON.parse(JSON.stringify(data));
          _data.childItems = matchedChildRes;
          _data.clickHandler = data.clickHandler;
          if(matchedChildRes.length){
            _data.showIcon = true;
            _data.noLeftMarginLabel = true;
            matchedResultsCurrentLevel.push(_data)
          }
          else{
            _data.showIcon = false;
            _data.noLeftMarginLabel = false;
            let str:string = '';
            if(_data.primaryTextLeft) str += _data.primaryTextLeft.trim();
            if(_data.primaryTextRight) str += _data.primaryTextRight.trim();
            if(str.toLowerCase().includes(val.toLowerCase())) matchedResultsCurrentLevel.push(_data);
          }
        }
      })
      return matchedResultsCurrentLevel;
    }

    private getLengthRecursively(list){
      let counter = 0;
      list.forEach(o=>{
        if(o.childItems && o.childItems.length){
          // Group/parent header: count only its leaf descendants, not the header itself,
          // so the results count reflects the number of actual selectable items (e.g. lots), not SKU + lots.
          counter += this.getLengthRecursively(o.childItems);
        } else {
          counter++;
        }
      })
      return counter;
    }

    private getSelectedItemsRecursively(list: MainCardViewDataModel[]){
      list.forEach(o=>{
        if(o.isItemSelectedForSelectionView){
          let selectedItem:MainCardViewDataModel = {
            ...o,
            endIconType: 'indegene-selectors-remove-icon',
            endIconCssClass: 'remove-icon',
            isMainToolTemplate: true,
            noSeparationLine: false,
            clickHandler: (id: string, event,target,data) => this._handleSelectionEvents(id,event,target,data),
        }
        this.selectedItems.push(selectedItem);
        this.tempSelectedItems.push(selectedItem);
        }
        if(o.childItems && o.childItems.length) this.getSelectedItemsRecursively(o.childItems);
      })
    }

    public doInfinite(eventDetails,event) {
      this.itemsToDisplay.push(...this.sliceItems(this.recordCount, 30));
      if(this.itemsToDisplay && this.itemsToDisplay.length>30) this.recordCount = this.itemsToDisplay.length;
      else this.recordCount = 30;
      event.target.complete();
    }

    private sliceItems(startIndex: number, count: number) {
      return this.filteredData.length < startIndex + count ? this.filteredData.slice(startIndex) : this.filteredData.slice(startIndex, startIndex + count);;
    }

    async openFilters(event) {
      if (this.viewData.isFilterPopoverActive) {
        const popoverFilter = await this.popover.create({
          component: MultiSelectPopover,
          componentProps: { root: this.viewData.filterPopoverData, multiselect: false, keyword: this.searchInput },
          event,
        });
        popoverFilter.present();
        popoverFilter.onDidDismiss().then(() => { });
      } else if(this.viewData.isFilterMenuActive) {
        // Implement Filter Menu(ind-filter-menu)
      }
    }

    private async initDetailData(item) {
      let detailViewData: MainToolTemplateDeatilData;
      if(this.viewData.isDetailPageEnabled && this.viewData.detailPageFetchingQuery) {
        let data = await this.getDataByFetchingQuery(item);
        switch (this.viewData.isFor) {
          case 'setBookingProducts' :
            detailViewData = {
              title: item.primaryTextRight,
              showLeftHeaderButton: this.device.isMobileDevice,
              leftHeaderBtnIcon: 'chevron-back-outline',
              showRightHeaderButton: false,
              isGeneralSectionEnabled: true,
              generalSectionTitle: this.translate.instant('PRODUCT_DETAILS'),
              isGridSectionEnabled: true,
              gridSectionTitle: this.translate.instant('LINE_ITEM_DETAILS'),
              attributeNamesForGeneralSection: [
                {attr:'productnumber', label:'Product ID'},
                {attr:'indskr_itemcount', label: 'Line Item Count'},
                {attr:'indskr_recordtypeoption_Formatted', label: 'Record Type'},
                {attr: 'indskr_organisationcode', label: 'Org Code'}],
              attributeNamesForGridSection: [
                {attr: 'ad.productnumber', label:'Product ID'},
                {attr: 'assoc.associatedproduct_Formatted', label: 'Product'},
                {attr: 'assoc.quantity', label: 'Quantity'},
                {attr: 'sequenceNumber', label: 'Sequence Number'}],
              rawData: data
            }
            break;
          case 'medicalInquiryProducts':
            detailViewData = {
              title: item.primaryTextRight,
              showLeftHeaderButton: this.device.isMobileDevice,
              leftHeaderBtnIcon: 'chevron-back-outline',
              showRightHeaderButton: false,
              isGeneralSectionEnabled: true,
              generalSectionTitle: this.translate.instant('PRODUCT_DETAILS'),
              isGridSectionEnabled: true,
              gridSectionTitle: this.translate.instant('LINE_ITEM_DETAILS'),
              attributeNamesForGeneralSection: [
                {attr:'productnumber', label:'Product ID'},
                {attr:'indskr_itemcount', label: 'Line Item Count'},
                {attr:'indskr_recordtypeoption_Formatted', label: 'Record Type'},
                {attr: 'indskr_organisationcode', label: 'Org Code'}],
              attributeNamesForGridSection: [
                {attr: 'ad.productnumber', label:'Product ID'},
                {attr: 'assoc.associatedproduct_Formatted', label: 'Product'},
                {attr: 'assoc.quantity', label: 'Quantity'},
                {attr: 'sequenceNumber', label: 'Sequence Number'}],
              rawData: data
            };
            break;
        }
        this.navService.setChildNavRoot(PageName.MainToolTemplateDetailsComponent, PageName.MainToolTemplateDetailsComponent, PageName.MainToolTemplateComponent, { for: this.viewData.isFor === 'medicalInquiryProducts' ? 'MedicalInquiryProduct' : 'SetBookingProduct', viewData: detailViewData });
        this.navService.setChildNavRightPaneView(true);
      }
    }

    private async getDataByFetchingQuery(item) {
      let detailData = [];
      let fetchXml = this.viewData.detailPageFetchingQuery;
      let targetEntity = 'products' 
      if(this.viewData.isFor == 'setBookingProducts' || this.viewData.isFor == 'medicalInquiryProducts') {
        targetEntity = 'products';
        fetchXml = fetchXml.replace('{productId}', item.id);
      }
      try {
        this.uiService.displayLoader();
        let data = await this.dynamics.executeFetchQuery(targetEntity, fetchXml);
        if(data) {
          data = data?.value ? data.value : data;
          detailData.push(data);
          this.uiService.dismissLoader();
        } else {
          console.error('No response from getDataByFetchingQuery : Main Tool Template');
          this.uiService.dismissLoader();
        }
      } catch (error) {
        this.uiService.dismissLoader();
        console.error(error);
      }
      return detailData;
    }

  trackById(index: number, item: any) { return item?.id || item?.ID || index; }
}
